import React, { useState } from 'react';
import { Loader2, Clock } from 'lucide-react';
import { useFieldHistory } from '../../hooks/useFieldHistory';
// completedAt is a full ISO instant. @see TaskCard — same field, same choice.
import { formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';

type HistoryFilter = 'all' | 'bol' | 'rental' | 'inspection' | 'timesheet';

const ALL_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'bol', label: 'BOLs' },
  { key: 'rental', label: 'Rentals' },
  { key: 'inspection', label: 'Inspections' },
  { key: 'timesheet', label: 'Timesheets' },
];

export default function HistoryPage() {
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const timeZone = useTenantTimeZone();
  const { hasVertical } = useAuth();
  const historyQuery = useFieldHistory(filter);
  const { data } = historyQuery;
  // A failed read is not "No items" (CLAUDE.md: three states, never two).
  const failed = queryLoadFailure(historyQuery) != null;
  const isLoading = historyQuery.isLoading && !failed;
  // Positive guards: BOLs for trucking, Rentals for surface rentals. A
  // trucking-only driver was offered a Rentals filter (fix/driver-app-trucking).
  const filters = ALL_FILTERS.filter(
    (f) => (f.key !== 'bol' || hasVertical('trucking')) && (f.key !== 'rental' || hasVertical('surface_rentals')),
  );

  const items = data?.items ?? [];

  return (
    <div className="px-4 pt-4 pb-4">
      <div>
        {/* The page title is the shell's, beside its back arrow (fix/field-home-trucking-rentals). */}
        <p className="text-xs text-fieldiq-text">Everything you've completed.</p>
      </div>

      {/* Filter pills */}
      <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {filters.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full border px-3.5 h-9 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-white text-fieldiq-dark border-white'
                  : 'bg-transparent text-white border-fieldiq-border hover:bg-fieldiq-card/40'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {failed ? (
        <div className="mt-5">
          <LoadFailureNotice error={queryFailureError(historyQuery)} subject="history" />
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <Clock size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
          <p className="text-sm text-fieldiq-text">No items.</p>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
          {items.map((h) => (
            <div key={h.id} className="flex items-start justify-between gap-3 p-3.5">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{h.title}</p>
                {h.subtitle && (
                  <p className="text-[11px] text-fieldiq-text truncate">{h.subtitle}</p>
                )}
                {/* No id line: it printed the item's key ("bol_" plus a UUID) under
                  * every completed load, which names nothing a driver can use. */}
              </div>
              <div className="text-right shrink-0">
                {h.completedAt && (
                  <p className="text-xs font-semibold tabular-nums">
                    {formatInstantDay(h.completedAt, timeZone, { month: 'short', day: 'numeric' }) ?? '—'}
                  </p>
                )}
                {h.meta && (
                  <p className="text-[11px] text-fieldiq-text mt-0.5">{h.meta}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
